import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  showPageSize?: boolean;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  showPageSize = true,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100]
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, totalItems);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  // Nothing to page through with no items or a single page.
  if (totalItems === 0 || totalPages <= 1) {
    return null;
  }

  return (
    <div className="pagination-bar mt-3">
      <button
        className="pagination-nav"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 0}
        aria-label="Previous"
      >
        <span aria-hidden="true">←</span>
        <span>Previous</span>
      </button>

      <nav aria-label="Page navigation">
        <ul className="pagination mb-0">
          {getVisiblePages().map((page, index) => (
            <li key={index} className={`page-item ${page === '...' ? 'disabled' : page === currentPage + 1 ? 'active' : ''}`}>
              {page === '...' ? (
                <span className="page-link">…</span>
              ) : (
                <button
                  className="page-link"
                  onClick={() => onPageChange(Number(page) - 1)}
                >
                  {page}
                </button>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <button
        className="pagination-nav"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages - 1}
        aria-label="Next"
      >
        <span>Next</span>
        <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

export default Pagination;
